import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconChecklist,
    IconChevronDown,
    IconChevronUp,
    IconDownload,
    IconFilePlus,
    IconLayoutGrid,
    IconList,
    IconLoader2,
    IconPencil,
    IconPlus,
    IconSchool,
    IconSearch,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits, formatBnDate } from '../../../utils/bnFormat';
import questionPaperService from '../../../services/questionPaperService';
import { useAuthStore } from '../../../store/authStore';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import DatePickerField from '../../../components/common/DatePickerField';
import SelectField from '../../../components/common/SelectField';
import { getTemplate } from '../question-papers/paperTemplates';
import MathText from '../question-papers/MathText';

// Grid cards preview this many questions before the "show more" toggle.
const PREVIEW_COUNT = 3;
// Matches the other My Files tabs — one page of results, filtered client-side.
const FETCH_LIMIT = 60;

// A picked date filters that WHOLE day (00:00 → 23:59.999).
const toDayRange = (date) => {
    if (!date) return [null, null];
    const start = new Date(date); start.setHours(0, 0, 0, 0);
    const end = new Date(date); end.setHours(23, 59, 59, 999);
    return [start, end];
};

/**
 * Saved question papers, shown as a My Files tab. Mirrors PrintMediaTab's
 * filter/toolbar pattern (search + day filter + sort + grid/list) while reusing
 * the question-paper card rendering (badges + KaTeX preview) from the old list page.
 */
export default function QuestionsTab({ officeSpaceId, onCreate }) {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const num = (n) => (isBn ? toBnDigits(n) : String(n));
    const navigate = useNavigate();

    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [view, setView] = useState(() => localStorage.getItem('qp-view') || 'grid');
    const [expandedIds, setExpandedIds] = useState(() => new Set());

    // Filters — search by title/institution + single-day range + sort (client-side).
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [dateRange, setDateRange] = useState([null, null]);
    const [sortOrder, setSortOrder] = useState('newest');
    const hasFilter = !!debouncedSearch || !!dateRange[0];

    const setViewMode = (v) => { setView(v); localStorage.setItem('qp-view', v); };
    const toggleExpanded = (id) => setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // The backend requires an officeSpaceId for non-admin users; it resolves
    // asynchronously, so on first render it may still be null — wait for it.
    const { user } = useAuthStore();
    const isAdminRole = user?.role === 'admin' || user?.role === 'superadmin';
    const canQuery = isAdminRole || !!officeSpaceId;

    const load = useCallback(async () => {
        if (!canQuery) return;
        setLoading(true);
        try {
            const res = await questionPaperService.list({
                page: 1,
                limit: FETCH_LIMIT,
                ...(officeSpaceId ? { officeSpaceId } : {}),
            });
            setPapers(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.loadFailed'), color: 'red' });
        } finally {
            setLoading(false);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [officeSpaceId, canQuery]);

    useEffect(() => { load(); }, [load]);

    // Client-side search + day filter + sort over the fetched page.
    const visiblePapers = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        const [from, to] = dateRange;
        let list = papers.filter((p) => {
            if (q) {
                const hay = `${p.title || ''} ${p.settings?.institution || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (from && to) {
                const ts = new Date(p.updatedAt).getTime();
                if (ts < from.getTime() || ts > to.getTime()) return false;
            }
            return true;
        });
        list = [...list].sort((a, b) => {
            const da = new Date(a.updatedAt).getTime();
            const db = new Date(b.updatedAt).getTime();
            return sortOrder === 'oldest' ? da - db : db - da;
        });
        return list;
    }, [papers, debouncedSearch, dateRange, sortOrder]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await questionPaperService.remove(deleteTarget.id);
            setPapers((prev) => prev.filter((p) => p.id !== deleteTarget.id));
            notifications.show({ title: t('common.success'), message: t('questionsCreate.toast.deleted'), color: 'green' });
            setDeleteTarget(null);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.toast.deleteFailed'), color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    const meta = (p) => {
        const count = Array.isArray(p.questions) ? p.questions.length : 0;
        const marks = p.settings?.totalMarks;
        const tp = getTemplate(p.settings?.template);
        return { count, marks, tplLabel: isBn ? tp.labelBn : tp.label };
    };

    const badges = (p) => {
        const { count, marks, tplLabel } = meta(p);
        const ps = p.settings?.pageSize;
        const psKey = ps === 'a5' ? 'pageA5' : ps === 'joint' ? 'pageJoint' : 'pageA4';
        return (
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    <IconChecklist size={12} /> {t('questionsCreate.selectedCount', { count: num(count) })}
                </span>
                {marks != null && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                        {t('questionsCreate.paper.fullMarks')}: {num(marks)}
                    </span>
                )}
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">{tplLabel}</span>
                <span className="rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                    {t(`questionsCreate.${psKey}`)}
                </span>
            </div>
        );
    };

    const institutionLine = (p) => {
        const name = (p.settings?.institution || '').trim();
        if (!name) return null;
        return (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                <IconSchool size={13} className="flex-none" />
                <span className="truncate">{name}</span>
            </p>
        );
    };

    const dateLabel = (p) => (isBn ? formatBnDate(p.updatedAt, { withYear: true }) : new Date(p.updatedAt).toLocaleDateString());

    const optionLetters = isBn ? ['ক', 'খ', 'গ', 'ঘ'] : ['a', 'b', 'c', 'd'];

    const questionPreview = (p) => {
        const qs = Array.isArray(p.questions) ? p.questions : [];
        if (qs.length === 0) return null;
        const isExpanded = expandedIds.has(p.id);
        const shown = isExpanded ? qs : qs.slice(0, PREVIEW_COUNT);
        const hiddenCount = qs.length - PREVIEW_COUNT;
        return (
            <div className="mt-3 rounded-lg bg-zinc-50 p-2.5 dark:bg-zinc-800/60">
                <ol className={`space-y-1.5 ${isExpanded ? 'max-h-60 overflow-y-auto pr-1' : ''}`}>
                    {shown.map((q, i) => (
                        <li key={q.id ?? i} className="flex items-start gap-1.5 text-xs leading-snug text-zinc-600 dark:text-zinc-300">
                            <span className="flex-none font-semibold text-zinc-400 dark:text-zinc-500">{num(i + 1)}.</span>
                            <span className="min-w-0 flex-1">
                                <span className={isExpanded ? 'block' : 'line-clamp-2'}>
                                    <MathText text={q.text} />
                                </span>
                                {isExpanded && Array.isArray(q.options) && q.options.length > 0 && (
                                    <span className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                                        {q.options.map((opt, oi) => (
                                            <span key={oi} className="flex items-start gap-1">
                                                <span className="flex-none">{optionLetters[oi] ?? num(oi + 1)})</span>
                                                <span className="min-w-0 flex-1"><MathText text={opt} /></span>
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </span>
                        </li>
                    ))}
                </ol>
                {hiddenCount > 0 && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(p.id); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                    >
                        {isExpanded ? (
                            <>{t('questionsCreate.showLess')} <IconChevronUp size={13} /></>
                        ) : (
                            <>{t('questionsCreate.showMoreQuestions', { count: num(hiddenCount) })} <IconChevronDown size={13} /></>
                        )}
                    </button>
                )}
            </div>
        );
    };

    const rowActions = (p) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/questions-create/${p.id}?export=1`); }}
                title={t('questionsCreate.download')}
                aria-label={t('questionsCreate.download')}
                className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-primary/10 hover:text-primary"
            >
                <IconDownload size={15} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/questions-create/${p.id}`); }}
                title={t('questionsCreate.editTitle')}
                aria-label={t('questionsCreate.editTitle')}
                className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-primary/10 hover:text-primary"
            >
                <IconPencil size={15} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                title={t('common.delete')}
                aria-label={t('common.delete')}
                className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
            >
                <IconTrash size={15} />
            </button>
        </div>
    );

    // Genuinely empty (no papers yet AND no active filter) → onboarding empty state.
    if (!loading && papers.length === 0 && !hasFilter) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-zinc-200 bg-white py-20 text-center dark:border-zinc-700 dark:bg-zinc-900">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-primary shadow-sm dark:bg-zinc-900">
                    <IconFilePlus size={28} stroke={1.6} />
                </span>
                <p className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">{t('questionsCreate.emptyTitle')}</p>
                <p className="max-w-xs text-xs text-zinc-400">{t('questionsCreate.emptyHint')}</p>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={onCreate}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                    >
                        <IconPlus size={16} /> {t('questionsCreate.createNew')}
                    </button>
                    {/* Typing your own questions is a separate page — keep the way there in plain sight */}
                    <button
                        type="button"
                        onClick={() => navigate('/questions-create/manual')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                        <IconPencil size={16} /> {t('questionsCreate.manual.writeOwn')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Search + day filter + sort + view toggle (matches the other My Files tabs) */}
            <div className="flex items-center gap-3 mb-5 flex-wrap">
                <div className="flex-1 min-w-[200px] flex items-center gap-2.5 h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
                    <IconSearch size={18} className="text-zinc-400 flex-shrink-0" />
                    <input
                        type="text"
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400"
                        placeholder={t('questionsCreate.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            aria-label={t('common.clear')}
                            className="flex-shrink-0 p-0.5 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <IconX size={16} />
                        </button>
                    )}
                </div>
                <div className="w-full sm:w-[180px]">
                    <DatePickerField
                        value={dateRange[0]}
                        onChange={(date) => setDateRange(toDayRange(date))}
                        clearable
                        disabled={{ after: new Date() }}
                        triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                    />
                </div>
                <div className="w-full sm:w-[160px]">
                    <SelectField
                        value={sortOrder}
                        onChange={setSortOrder}
                        options={[{ value: 'newest', label: t('common.newest') }, { value: 'oldest', label: t('common.oldest') }]}
                        triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                    />
                </div>
                <div className="flex gap-1 bg-[#F1EFEA] dark:bg-zinc-800 rounded-[10px] p-1 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        aria-label={t('questionsCreate.viewList')}
                        className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${view === 'list' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                    >
                        <IconList size={17} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('grid')}
                        aria-label={t('questionsCreate.viewGrid')}
                        className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${view === 'grid' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                    >
                        <IconLayoutGrid size={17} />
                    </button>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/questions-create/manual')}
                    className="flex flex-shrink-0 items-center gap-2 rounded-[11px] border border-zinc-200 bg-white px-4 h-11 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    <IconPencil size={17} /> {t('questionsCreate.manual.writeOwn')}
                </button>
                <button
                    type="button"
                    onClick={onCreate}
                    className="flex flex-shrink-0 items-center gap-2 rounded-[11px] bg-primary px-4 h-11 text-sm font-semibold text-white shadow-sm shadow-primary/30 transition-colors hover:bg-primary/90"
                >
                    <IconPlus size={17} /> {t('questionsCreate.createNew')}
                </button>
            </div>

            {loading ? (
                <div className="flex min-h-[30vh] items-center justify-center text-zinc-500">
                    <IconLoader2 size={18} className="animate-spin mr-2" /> {t('questionsCreate.loading')}
                </div>
            ) : visiblePapers.length === 0 ? (
                <div className="flex min-h-[30vh] items-center justify-center rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {t('questionsCreate.noResults')}
                </div>
            ) : view === 'grid' ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visiblePapers.map((p) => (
                        <div
                            key={p.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/questions-create/${p.id}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/questions-create/${p.id}`); }}
                            className="group flex cursor-pointer flex-col rounded-2xl border border-zinc-200 bg-white p-4 transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <h3 className="line-clamp-2 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                    {p.title || t('questionsCreate.paper.defaultTitle')}
                                </h3>
                                <div className="flex-none opacity-0 transition-opacity group-hover:opacity-100">{rowActions(p)}</div>
                            </div>
                            {institutionLine(p)}
                            <div className="mt-2">{badges(p)}</div>
                            {questionPreview(p)}
                            <p className="mt-auto pt-2 text-[11px] text-zinc-400">{dateLabel(p)}</p>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="divide-y divide-zinc-100 overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:divide-zinc-800 dark:border-zinc-700 dark:bg-zinc-900">
                    {visiblePapers.map((p) => (
                        <div
                            key={p.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/questions-create/${p.id}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/questions-create/${p.id}`); }}
                            className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        >
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                    {p.title || t('questionsCreate.paper.defaultTitle')}
                                </h3>
                                {institutionLine(p)}
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{badges(p)}</div>
                            </div>
                            <p className="hidden flex-none text-[11px] text-zinc-400 sm:block">{dateLabel(p)}</p>
                            {rowActions(p)}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmDialog
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title={t('questionsCreate.deleteConfirmTitle')}
                description={deleteTarget?.title || t('questionsCreate.paper.defaultTitle')}
                confirmLabel={t('common.delete')}
            />
        </>
    );
}
